import { useState } from "react";
import { Layout } from "@/components/Layout";
import api, { formatApiError } from "@/lib/api";
import { CreditCard, Lightning, CheckCircle } from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";
import { Navigate } from "react-router-dom";

const FEATURES = [
  "Full training video library",
  "Download center: templates, agreements, safety docs",
  "Member-only equipment pricing",
  "5-day early access to used / demo / refurbished inventory",
  "Private support tickets with Dry Ice Bulls admins",
  "Member announcements and early product news",
  "Priority access to Phase 2 supplier network",
  "First in line for Phase 3 Certified Contractor Program",
];

export default function Billing() {
  const { user, isMember } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  if (isMember) return <Navigate to="/dashboard" replace />;

  const checkout = async () => {
    setLoading(true); setError("");
    try {
      const { data } = await api.post("/payments/checkout", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (err) {
      setError(formatApiError(err));
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 lg:px-8 py-16">
        <div className="text-center mb-12">
          <div className="section-label mb-3">— ACTIVATE MEMBERSHIP</div>
          <h1 className="font-chivo font-black text-4xl lg:text-6xl text-white uppercase tracking-tighter leading-none" data-testid="billing-headline">Unlock The Portal</h1>
          <p className="font-plex text-[#9CA3AF] mt-6 max-w-2xl mx-auto">
            One annual payment unlocks every feature. Cancel auto-renewal anytime.
          </p>
        </div>

        <div className="surface p-8 lg:p-12 grid grid-cols-1 lg:grid-cols-12 gap-8" data-testid="billing-card">
          <div className="lg:col-span-5 border-r-0 lg:border-r border-[#262626] lg:pr-8">
            <div className="section-label mb-3">PLAN</div>
            <div className="flex items-baseline gap-2 mb-1">
              <span className="font-chivo font-black text-7xl text-white tracking-tighter">$399</span>
              <span className="font-mono text-sm text-[#52525B]">/YEAR</span>
            </div>
            <div className="font-mono text-xs text-[#00D4FF] tracking-widest uppercase mb-6">Annual Subscription</div>
            <button onClick={checkout} disabled={loading} className="btn-primary w-full" data-testid="checkout-cta">
              <CreditCard size={16} weight="bold" /> {loading ? "Loading checkout..." : "Continue to Stripe"}
            </button>
            {error && <div className="text-sm font-mono text-[#EF4444] mt-3">{error}</div>}
            <p className="font-mono text-[10px] text-[#52525B] mt-4 leading-relaxed">
              Logged in as <span className="text-[#9CA3AF]">{user?.email}</span>. Payment processed by Stripe. Test mode card: 4242 4242 4242 4242.
            </p>
          </div>
          <div className="lg:col-span-7">
            <div className="section-label mb-4">INCLUDED</div>
            <ul className="space-y-3">
              {FEATURES.map((f) => (
                <li key={f} className="flex items-start gap-3 font-plex text-sm text-white">
                  <CheckCircle size={18} weight="fill" className="text-[#00D4FF] shrink-0 mt-0.5" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </Layout>
  );
}
